import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Download } from "lucide-react";
import { api, formatDateTimeShort } from "../../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

function Messages() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/messages").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const read = async (m) => { if (!m.read) { await api.patch(`/admin/messages/${m.id}/read`); load(); } };
  const del = async (id) => { if (!window.confirm("Supprimer ce message ?")) return; await api.delete(`/admin/messages/${id}`); toast.success("Message supprimé"); load(); };

  if (!items.length) return <p className="font-sub text-sm text-slate-500 py-10 text-center" data-testid="messages-empty">Aucun message pour le moment.</p>;
  return (
    <div className="space-y-3" data-testid="messages-list">
      {items.map((m) => (
        <div key={m.id} onClick={() => read(m)} data-testid={`message-${m.id}`}
          className={`bg-white rounded-xl border p-5 cursor-pointer ${m.read ? "border-[#E2E8F0]" : "border-[#E11D48]/50"}`}>
          <div className="flex justify-between gap-3 mb-2">
            <div>
              <p className="font-headline font-bold text-[#0A0A0A]">{!m.read && <span className="inline-block w-2 h-2 rounded-full bg-[#E11D48] mr-2" />}{m.subject}</p>
              <p className="font-sub text-xs text-slate-500">{m.name} · <a href={`mailto:${m.email}`} className="hover:text-[#E11D48]">{m.email}</a>{m.phone && ` · ${m.phone}`} · {formatDateTimeShort(m.created_at)}</p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); del(m.id); }} data-testid={`message-delete-${m.id}`} className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
          </div>
          <p className="font-body text-sm text-slate-700 whitespace-pre-wrap text-justify">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

function Subscribers() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/subscribers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (email) => { if (!window.confirm(`Retirer ${email} ?`)) return; await api.delete(`/admin/subscribers/${encodeURIComponent(email)}`); toast.success("Abonné retiré"); load(); };
  const exportCsv = () => {
    const csv = "email,date\n" + items.map((s) => `${s.email},${s.created_at}`).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "abonnes-infolettre.csv";
    a.click();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="font-sub text-sm text-slate-600" data-testid="subscribers-count">{items.length} abonné(s)</p>
        {items.length > 0 && <button onClick={exportCsv} data-testid="subscribers-export" className="meta-label flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2 rounded-full"><Download size={14} /> Exporter CSV</button>}
      </div>
      <div className="bg-white rounded-xl border border-[#E2E8F0] divide-y" data-testid="subscribers-list">
        {items.map((s) => (
          <div key={s.email} className="flex items-center justify-between px-5 py-3">
            <span className="font-sub text-sm flex items-center gap-2"><Mail size={14} className="text-slate-400" /> {s.email}</span>
            <span className="flex items-center gap-4">
              <span className="font-sub text-xs text-slate-400">{formatDateTimeShort(s.created_at)}</span>
              <button onClick={() => del(s.email)} data-testid={`subscriber-delete-${s.email}`} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Inbox() {
  useEffect(() => { document.title = "Messages — Admin"; }, []);
  return (
    <div className="p-4 sm:p-8 max-w-5xl" data-testid="admin-inbox">
      <h1 className="font-headline text-3xl font-black text-[#0A0A0A] mb-6">Messages & infolettre</h1>
      <Tabs defaultValue="messages">
        <TabsList className="mb-6">
          <TabsTrigger value="messages" data-testid="inbox-tab-messages">Messages de contact</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="inbox-tab-subscribers">Abonnés infolettre</TabsTrigger>
        </TabsList>
        <TabsContent value="messages"><Messages /></TabsContent>
        <TabsContent value="subscribers"><Subscribers /></TabsContent>
      </Tabs>
    </div>
  );
}
